/**
 * Typed wrappers for the Phase 2 internal-admin endpoints.
 *
 * All of these are internal-only server-side (portal_identity.require_internal);
 * the SPA's route guards are a UX nicety, never the boundary. See
 * docs/superpowers/specs/2026-07-28-customer-portal-phase2-design.md §3.
 */
import { apiFetch } from "./client";

export interface Customer {
  client_id: string;
  client_name: string;
  legal_name: string | null;
  active: boolean;
  api_enabled: boolean;
  report_types: string[];
  sftp_username: string | null;
  created_at: string;
  admin_email: string | null;
}

/** Live Cognito state, resolved server-side. `null` means the lookup failed
 *  and no recovery action should be offered for that row. */
export type AccessStatus = "pending_first_login" | "active" | "disabled";

export type RecoveryAction =
  "resend_invite" | "reset_password" | "reset_mfa" | "sign_out";

/** Every resend reissues the temporary password, so a user working from an
 *  older invitation email is silently stuck unless we say this. */
export const RECOVERY_SENT_NOTE =
  "Sent — any earlier temporary password for this user is now invalid.";

export const RECOVERY_STALE_NOTE =
  "This user's status changed. Reload the page and try again.";

/** Clearing a device is privilege removal, not a resend -- say what it costs
 *  the user, since they cannot undo it themselves. */
export const MFA_RESET_NOTE =
  "Authenticator deleted and the user signed out — they enroll a new one at next login.";

/** The delete succeeded and the sign-out did not. The factor is gone, so the
 *  Reset MFA control no longer applies to this row -- Sign out is how the
 *  operator finishes the job. */
export const MFA_SIGNOUT_FAILED_NOTE =
  "Authenticator deleted, but signing the user out failed — use Sign out to finish.";

export const SIGNED_OUT_NOTE =
  "Signed out — their next request returns them to login.";

/** A short label for the Status column, alongside the active/deactivated
 *  state -- so a row with no recovery button (Cognito unreachable, or a
 *  never-logged-in user) reads as *why*, not as an unexplained gap. `null`
 *  from access_status means the Cognito lookup failed, not that the account
 *  is healthy, so it gets its own visible label rather than blending in
 *  with a normal active row. Shared by StaffPage and TeamPage so the two
 *  stay worded the same. */
export function accessStatusLabel(status: AccessStatus | null): string | null {
  if (status === "pending_first_login") return "Never logged in";
  if (status === null) return "Status unavailable";
  return null;
}

export interface StaffUser {
  user_id: string;
  email: string;
  active: boolean;
  created_at: string;
  access_status: AccessStatus | null;
}

export type AuditAction =
  | "client.onboard"
  | "client.activate"
  | "client.deactivate"
  | "client.api_enable"
  | "client.api_disable"
  | "client.config_update"
  | "client.f2r_rotate"
  | "client.sftp_key_add"
  | "client.sftp_key_delete"
  | "client.slack_set" | "client.slack_clear" | "client.slack_test" | "report.withdraw"
  | "report.fintrac_delete"
  | "user.promote"
  | "user.resend_invite"
  | "user.reset_password"
  | "user.reset_mfa"
  | "user.sign_out"
  | "staff.invite"
  | "staff.deactivate"
  | "staff.resend_invite"
  | "staff.reset_password"
  | "staff.reset_mfa"
  | "staff.sign_out";

export interface AuditEntry {
  audit_id: string;
  actor_user_id: string;
  actor_email: string;
  action: AuditAction;
  client_id: string | null;
  target_user_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

export const AUDIT_ACTIONS: AuditAction[] = [
  "client.onboard", "client.activate", "client.deactivate",
  "client.api_enable", "client.api_disable",
  "client.config_update", "client.f2r_rotate",
  "client.sftp_key_add", "client.sftp_key_delete",
  "client.slack_set", "client.slack_clear", "client.slack_test",
  "report.withdraw",
  "report.fintrac_delete",
  "user.promote",
  "user.resend_invite", "user.reset_password",
  "user.reset_mfa", "user.sign_out",
  "staff.invite", "staff.deactivate",
  "staff.resend_invite", "staff.reset_password",
  "staff.reset_mfa", "staff.sign_out",
];

/** Human labels for the audit vocabulary -- "client.api_disable" is a key,
 *  not something to show an operator. */
export const AUDIT_LABELS: Record<AuditAction, string> = {
  "client.onboard": "Onboarded customer",
  "client.activate": "Activated customer",
  "client.deactivate": "Deactivated customer",
  "client.api_enable": "Enabled API",
  "client.api_disable": "Disabled API",
  "client.config_update": "Updated customer settings",
  "client.f2r_rotate": "Rotated FINTRAC credentials",
  "client.sftp_key_add": "Added SFTP key",
  "client.sftp_key_delete": "Removed SFTP key",
  "client.slack_set": "Set Slack channel",
  "client.slack_clear": "Removed Slack channel",
  "client.slack_test": "Tested Slack channel",
  "report.withdraw": "Recorded FINTRAC withdrawal",
  "report.fintrac_delete": "Deleted report at FINTRAC",
  "user.promote": "Reassigned Customer Admin",
  "user.resend_invite": "Resent user invitation",
  "user.reset_password": "Sent user password reset",
  "user.reset_mfa": "Cleared user authenticator",
  "user.sign_out": "Signed user out",
  "staff.invite": "Invited staff member",
  "staff.deactivate": "Deactivated staff member",
  "staff.resend_invite": "Resent staff invitation",
  "staff.reset_password": "Sent staff password reset",
  "staff.reset_mfa": "Cleared staff authenticator",
  "staff.sign_out": "Signed staff member out",
};

export interface PatchCustomerBody {
  active?: boolean;
  api_enabled?: boolean;
  revoke?: boolean;
}

export interface PatchCustomerResult {
  customer: Customer;
  applied: {
    active?: boolean;
    api_enabled?: boolean;
    api_credentials?: {
      app_client_id?: string;
      client_secret?: string;
      token_endpoint?: string;
      scope?: string;
    };
  };
}

export function listCustomers(): Promise<{ customers: Customer[] }> {
  return apiFetch<{ customers: Customer[] }>("/portal/customers");
}

export function patchCustomer(clientId: string, body: PatchCustomerBody): Promise<PatchCustomerResult> {
  return apiFetch<PatchCustomerResult>(`/portal/customers/${clientId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function promoteUser(userId: string): Promise<{ user_id: string; demoted: string | null }> {
  return apiFetch(`/portal/users/${userId}/promote`, { method: "PATCH" });
}

export function listStaff(): Promise<{ staff: StaffUser[] }> {
  return apiFetch<{ staff: StaffUser[] }>("/portal/staff");
}

export function inviteStaff(email: string): Promise<StaffUser> {
  return apiFetch("/portal/staff", { method: "POST", body: JSON.stringify({ email }) });
}

export function deactivateStaff(userId: string): Promise<{ user_id: string }> {
  return apiFetch(`/portal/staff/${userId}`, { method: "DELETE" });
}

/** `factor_deleted`/`signed_out` are present only for reset_mfa and sign_out.
 *  A reset that deleted the factor but failed to end the session comes back
 *  200 with `signed_out: false` -- the operator finishes it with sign_out. */
export interface RecoveryResult {
  action_taken: RecoveryAction;
  factor_deleted?: boolean;
  signed_out?: boolean;
}

export function resendStaffAccess(
  userId: string, action: RecoveryAction,
): Promise<RecoveryResult> {
  return apiFetch(`/portal/staff/${userId}/resend-access`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export function resendUserAccess(
  userId: string, action: RecoveryAction,
): Promise<RecoveryResult> {
  return apiFetch(`/portal/users/${userId}/resend-access`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

/** Everything the settings page reads. `updated_at` is round-tripped on
 *  PATCH so a concurrent edit is a 409 rather than a silent clobber. */
export interface CustomerDetail extends Customer {
  contact_email: string | null;
  threshold_cad: string;
  eftr_directions: string[];
  deadline_alert_days: number;
  window_start_time: string;
  reporting_entity_number: string | null;
  submitting_reporting_entity_number: string | null;
  reporting_entity_contact_id: string | null;
  activity_sector_code: number | null;
  sftp_allowed_cidrs: string[];
  cognito_app_client_id: string | null;
  updated_at: string;
  slack_webhook_hint: string | null;
  slack_webhook_set_at: string | null;
  slack_webhook_status: "ok" | "broken" | null;
}

export interface SettingsPatchBody {
  client_name?: string;
  legal_name?: string | null;
  contact_email?: string | null;
  reporting_entity_number?: string | null;
  submitting_reporting_entity_number?: string | null;
  reporting_entity_contact_id?: string | null;
  activity_sector_code?: number;
  report_types?: string[];
  threshold_cad?: string;
  eftr_directions?: string[];
  deadline_alert_days?: number;
  window_start_time?: string;
  sftp_allowed_cidrs?: string[];
  cancel_in_flight?: boolean;
  updated_at?: string;
}

export interface DryRunPreview {
  diff: Record<string, { from: unknown; to: unknown }>;
  in_flight: Record<string, {
    candidates: Record<string, number>;
    pending_txn_rows: number;
  }>;
  newly_required_fields: Array<{ report_type: string; fields: string[] }>;
  warnings: string[];
}

export interface SftpKey {
  key_id: string;
  date_imported: string | null;
}

export function getCustomer(clientId: string): Promise<{ customer: CustomerDetail }> {
  return apiFetch(`/portal/customers/${clientId}`);
}

export function previewCustomerConfig(
  clientId: string, body: SettingsPatchBody,
): Promise<DryRunPreview> {
  return apiFetch(`/portal/customers/${clientId}?dry_run=true`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function patchCustomerConfig(
  clientId: string, body: SettingsPatchBody,
): Promise<Omit<PatchCustomerResult, "customer"> & {
  // This PATCH always returns the full detail row (same _get_client query
  // as GET /portal/customers/{id}), not just the list-shaped `Customer` --
  // see fn_portal_customers/handler.py `_patch_customer`'s `_get_client`
  // call. Overridden here rather than widened on `PatchCustomerResult`
  // itself, since `patchCustomer` (the other caller of that shared type)
  // only reads `Customer` fields and its tests fix `Customer`-shaped
  // fixtures.
  customer: CustomerDetail;
  applied: {
    cancelled?: { candidates: number; txn_rows: number };
    /** Field names actually changed by this PATCH. */
    config?: string[];
    /** Present only when the SFTP firewall ranges saved but did not
     *  reconcile -- the only way an operator learns the change didn't take
     *  effect on the firewall. */
    warnings?: string[];
  };
}> {
  return apiFetch(`/portal/customers/${clientId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function listSftpKeys(clientId: string): Promise<{ keys: SftpKey[] }> {
  return apiFetch(`/portal/customers/${clientId}/sftp-keys`);
}

export function addSftpKey(clientId: string, publicKey: string): Promise<{ key_id: string }> {
  return apiFetch(`/portal/customers/${clientId}/sftp-keys`, {
    method: "POST",
    body: JSON.stringify({ public_key: publicKey }),
  });
}

export function deleteSftpKey(clientId: string, keyId: string): Promise<{ key_id: string }> {
  return apiFetch(`/portal/customers/${clientId}/sftp-keys/${keyId}`, { method: "DELETE" });
}

/** Write-only: the response confirms the rotation, never the values. */
export function rotateF2rCredentials(
  clientId: string,
  creds: { oauth_client_id: string; oauth_client_secret: string; submitter_id?: string },
): Promise<{ rotated: boolean }> {
  return apiFetch(`/portal/customers/${clientId}/f2r-credentials`, {
    method: "POST",
    body: JSON.stringify(creds),
  });
}

/** Write-only like the F2R credentials: the URL goes in, only a 4-char hint comes back. */
export function setSlackWebhook(clientId: string, url: string): Promise<{ hint: string }> {
  return apiFetch(`/portal/customers/${clientId}/slack`, {
    method: "PUT",
    body: JSON.stringify({ url }),
  });
}

export function clearSlackWebhook(clientId: string): Promise<{ cleared: boolean }> {
  return apiFetch(`/portal/customers/${clientId}/slack`, { method: "DELETE" });
}

export function testSlackWebhook(
  clientId: string,
): Promise<{ delivered: boolean; error: string | null }> {
  return apiFetch(`/portal/customers/${clientId}/slack/test`, { method: "POST" });
}

export interface WithdrawalBody {
  withdrawn_at: string;
  reason: "DUPLICATE" | "SUBMITTED_IN_ERROR";
  note?: string;
}

/** Records a report FINTRAC already deleted (out of band). Internal-only;
 *  the backend 403s anyone else and 409s a report FINTRAC does not hold. */
export function recordWithdrawal(candidateId: string, clientId: string, body: WithdrawalBody) {
  return apiFetch(
    `/portal/reports/${candidateId}/withdrawal?client_id=${encodeURIComponent(clientId)}`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export interface DeletionPreviewTxn {
  reference: string;
  transaction_date_time: string;
  amount: number | string;
  currency: string;
  amount_cad: number | string;
  sender: string | null;
  receiver: string | null;
}

export interface DeletionPreview {
  candidate_id: string;
  report_type: string;
  fintrac_reference_num: string | null;
  transaction_count: number;
  deletable_until: string | null;
  status: string;
  transactions: DeletionPreviewTxn[];
  split_siblings: string[];
  reasons: { code: "DUPLICATE" | "SUBMITTED_IN_ERROR"; label: string; help: string }[];
  ack_version: string;
  ack_text: string;
  confirm_text: string;
  fingerprint: string;
}

export interface DeletionBody {
  reason: "DUPLICATE" | "SUBMITTED_IN_ERROR";
  note?: string;
  ack_version: string;
  fingerprint: string;
  confirm_reference: string;
}

/** Customer Admin only; scoped by the caller's JWT (spec 2026-10-09 §5.3). */
export function getDeletionPreview(candidateId: string): Promise<DeletionPreview> {
  return apiFetch(`/portal/reports/${candidateId}/deletion-preview`);
}

/** 200 = deleted (returns the report detail); 202 = outcome unknown. */
export function deleteFromFintrac(candidateId: string, body: DeletionBody) {
  return apiFetch<{ outcome?: string; error?: string }>(
    `/portal/reports/${candidateId}/fintrac-deletion`,
    { method: "POST", body: JSON.stringify(body) },
  );
}
